import React, { type ReactElement } from 'react';
import {
  Outlet,
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import * as Sentry from '@sentry/browser';

import { ProductLogo } from './components/Logo';
import { LanguageSelect } from './components/LanguageSelect';
import { LanguageProvider } from './polyglot';
import { SessionProvider } from './contexts/sessionStore';
import { EnvironmentProvider } from './contexts/environment';
import { ChangePasswordWithSSO } from './views/ChangePassword';
import { CreatePasswordWithSSO } from './views/CreatePassword';
import { ForgotPasswordWithSSO } from './views/ForgotPassword';
import { LoginPageWithSSO } from './views/LoginPage';
import { RegisterPageWithSSO } from './views/Register';
import { SelectAccountWithSSO } from './views/SelectAccount';
import { InviteSignUpWithSSO } from './views/InviteSignUp';
import { TwoFactorCodeWithSSO } from './views/TwoFactorCode';
import { ExternalRedirect } from './views/ExternalRedirect';
import { AuthOptionsPage } from './views/AuthOptionsPage';
import { SetupPasskey } from './views/SetupPasskey';

import {
  GlobalStyles,
  RootBox,
  ViewBox,
} from './styles';

if (process.env['NODE_ENV'] === 'production') {
  Sentry.init({
    dsn: process.env['SENTRY_DSN'],
    release: process.env['VERSION'],
  });
}

export function App(): ReactElement {
  return (
    <BrowserRouter>
      <GlobalStyles />
      <LanguageProvider>
        <EnvironmentProvider>
          <SessionProvider>
            <Routes>
              <Route path="/" element={<Navigate to="/login" />} />
              <Route path="/register" element={<RegisterPageWithSSO />} />
              <Route
                element={(
                  <RootBox>
                    <ViewBox>
                      <ProductLogo format="square" />
                      <Outlet />
                    </ViewBox>
                    <LanguageSelect />
                  </RootBox>
                )}
              >
                <Route path="/login" element={<LoginPageWithSSO />} />
                <Route path="/select-account" element={<SelectAccountWithSSO />} />
                <Route path="/confirmation-code" element={<TwoFactorCodeWithSSO />} />
                <Route path="/auth-options" element={<AuthOptionsPage />} />
                <Route path="/setup-passkey" element={<SetupPasskey />} />
                <Route path="/reset-password" element={<ForgotPasswordWithSSO />} />
                <Route path="/reset-password/:token" element={<ChangePasswordWithSSO />} />
                <Route path="/create-password/:token/:email/:account" element={<CreatePasswordWithSSO />} />
                <Route path="/invite/:token" element={<InviteSignUpWithSSO />} />
                <Route path="/authorize" element={<ExternalRedirect to="/" />} />
              </Route>
            </Routes>
          </SessionProvider>
        </EnvironmentProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
